import { escapeHtml } from "../util/escape-html.js";
/**
 * Per-cause guidance, keyed by the kernel's `StorageRecoveryCode`. Environmental,
 * by-design conditions (Safari Private Browsing has no OPFS at all — see #1661) get
 * copy that names the real remedy instead of implying a retry will fix them; transient
 * causes (#1653 starved worker chunk, #1660 writer-lease conflict) get retry-first copy.
 * Unknown/future codes fall back to the engine-provided message.
 */
const RECOVERY_GUIDANCE = {
    "opfs-unavailable": "This browser session does not provide persistent storage — private or incognito " +
        "windows on some browsers never do. If this is a private window, reopen the app in a " +
        "standard window. Otherwise, retry once the browser has settled.",
    "quota-exceeded": "The browser's storage quota for this app is exhausted. Free up site storage in the " +
        "browser's settings, then retry.",
    "writer-conflict": "Another window or tab currently owns this app's local storage. Close other tabs " +
        "running the app, then retry.",
};
const STARTING_MESSAGE = "The local database is starting. Retry when the browser is ready.";
const DEFAULT_MESSAGE = "The local database could not start. Retry, or reload the app.";
/**
 * What still works, and what does not, without the durable local cache (#1661).
 *
 * This is the user-facing statement of the kernel's own gating split — see
 * `awaitTransportServicesReady` vs `awaitNormalServicesReady` in
 * `system/pwa/src/kernel-runtime.ts`. Keep the two in step: anything moved across that
 * boundary changes which column it belongs in here.
 *
 * It is rendered for the `banner` variant only. In the `blocking` variant the shell
 * failed to start at all, so claiming stages and live node data are "still available"
 * would be false — see the `variant` setter.
 */
const AVAILABLE_WITHOUT_STORAGE = [
    "Pairing this browser and staying signed in",
    "Opening installed stages",
    "Live data read from your node",
];
const UNAVAILABLE_WITHOUT_STORAGE = [
    "Offline history — conversations, notifications and settings are not kept on this device",
    "Queued sends and drafts — nothing is held for later delivery, so nothing can be silently lost",
    "The offline wallet mirror — balances and payment history are read live or not at all",
];
/**
 * `<node-storage-recovery>` — the shell's storage-recovery surface (#1697). Mounted by
 * `app-shell.ts` into the stage outlet when shell startup fails AND the kernel's ungated
 * `client-storage.state.v1` command reports `recovery-required` — i.e. the post-pairing
 * "stuck shell" state where the readiness gate rejects every query. It complements (does
 * not replace) the pairing view's own error branch, which covers pre-pairing bootstrap
 * failures on a surface that can never be on screen at the same time as this one.
 *
 * Renders light DOM on purpose: inside the shell's shadow tree the adopted skin sheet
 * (`shell-utility.css` `.node-card` family + the `.node-storage-recovery-*` rules added
 * with this element) styles it with real theme tokens, so it follows the resolved
 * light/dark theme like every other shell-chrome card.
 */
export class NodeStorageRecoveryElement extends HTMLElement {
    #busy = false;
    #state;
    #variant = "blocking";
    /** See `StorageRecoveryVariant`. Defaults to `blocking`, the #1697 surface, so an
     * unset variant never over-claims what still works. */
    set variant(value) {
        this.#variant = value;
        this.render();
    }
    get variant() {
        return this.#variant;
    }
    set state(value) {
        this.#state = value;
        this.render();
    }
    get state() {
        return this.#state;
    }
    /** True while a retry command is in flight — disables both actions and marks the card
     * `aria-busy` so assistive tech announces the pending operation. */
    set busy(value) {
        this.#busy = value;
        this.#syncBusy();
    }
    get busy() {
        return this.#busy;
    }
    connectedCallback() {
        this.render();
    }
    render() {
        const state = this.#state;
        if (state === undefined || state.status === "ready") {
            this.replaceChildren();
            return;
        }
        // Preserve the diagnostics disclosure across re-renders (a retry returning a fresh
        // recovery state re-renders this card; collapsing an open panel mid-read would lose
        // the user's place).
        const diagnosticsOpen = this.querySelector("details")?.open === true;
        const message = state.status === "starting" ? STARTING_MESSAGE : guidanceFor(state);
        const banner = this.#variant === "banner";
        // `alert` interrupts; `status` is polite. A blocking card IS the whole view and
        // warrants the interruption, a banner over a working shell does not.
        const liveness = banner ? "status" : "alert";
        this.innerHTML = `
      <section class="node-card node-storage-recovery" aria-label="Storage recovery"
        data-storage-recovery data-storage-recovery-variant="${banner ? "banner" : "blocking"}"
        data-storage-recovery-code="${escapeHtml(state.code ?? "unknown")}">
        <h2 class="node-card-title">${escapeHtml(banner ? bannerTitleFor(state) : "Local storage needs attention")}</h2>
        <p role="${liveness}" data-recovery-message>${escapeHtml(message)}</p>
        ${banner ? availabilityMarkup() : ""}
        <div class="node-storage-recovery-actions">
          <button type="button" class="node-storage-recovery-btn" data-recovery-action="retry">Retry</button>
          <button type="button" class="node-storage-recovery-btn node-storage-recovery-btn-secondary" data-recovery-action="reload">Reload app</button>
        </div>
        ${diagnosticsMarkup(state, diagnosticsOpen)}
      </section>
    `;
        this.querySelectorAll("[data-recovery-action]").forEach((button) => {
            button.addEventListener("click", () => {
                if (this.#busy)
                    return;
                this.dispatchEvent(new CustomEvent("storage-recovery-action", {
                    bubbles: true,
                    composed: true,
                    detail: { action: button.dataset.recoveryAction === "reload" ? "reload" : "retry" },
                }));
            });
        });
        this.#syncBusy();
    }
    #syncBusy() {
        const section = this.querySelector(".node-storage-recovery");
        section?.setAttribute("aria-busy", this.#busy ? "true" : "false");
        this.querySelectorAll("[data-recovery-action]").forEach((button) => {
            button.disabled = this.#busy;
        });
    }
}
/**
 * #1661 — for the one code where the cause is the browsing mode itself rather than a
 * fault, the banner heading says so. "Local storage needs attention" implies the user
 * has something to fix; in a private window there is nothing wrong and nothing to repair.
 */
function bannerTitleFor(state) {
    return state.code === "opfs-unavailable"
        ? "This browser cannot store data on this device"
        : "Local storage needs attention";
}
function availabilityMarkup() {
    return `
    <h3 class="node-storage-recovery-subtitle">Still available</h3>
    <ul data-storage-recovery-available>${listItems(AVAILABLE_WITHOUT_STORAGE)}</ul>
    <h3 class="node-storage-recovery-subtitle">Unavailable until local storage works</h3>
    <ul data-storage-recovery-unavailable>${listItems(UNAVAILABLE_WITHOUT_STORAGE)}</ul>
  `;
}
function listItems(entries) {
    return entries.map((entry) => `<li>${escapeHtml(entry)}</li>`).join("");
}
function guidanceFor(state) {
    const guidance = state.code === undefined ? undefined : RECOVERY_GUIDANCE[state.code];
    if (guidance !== undefined)
        return guidance;
    if (state.message !== undefined && state.message.trim() !== "")
        return state.message;
    return DEFAULT_MESSAGE;
}
function diagnosticsMarkup(state, open) {
    const rows = [];
    if (state.code !== undefined) {
        rows.push(`<dt>Code</dt><dd data-diagnostic="code">${escapeHtml(state.code)}</dd>`);
    }
    if (state.message !== undefined && state.message.trim() !== "") {
        rows.push(`<dt>Engine</dt><dd data-diagnostic="message">${escapeHtml(state.message)}</dd>`);
    }
    if (state.schemaVersion !== undefined) {
        rows.push(`<dt>Schema</dt><dd data-diagnostic="schema-version">${escapeHtml(String(state.schemaVersion))}</dd>`);
    }
    if (state.storageGeneration !== undefined) {
        rows.push(`<dt>Generation</dt><dd data-diagnostic="storage-generation">${escapeHtml(String(state.storageGeneration))}</dd>`);
    }
    if (rows.length === 0)
        return "";
    return `
    <details class="node-storage-recovery-diagnostics"${open ? " open" : ""}>
      <summary>Diagnostics</summary>
      <dl>${rows.join("")}</dl>
    </details>
  `;
}
//# sourceMappingURL=storage-recovery.js.map